/**
 * Les mots de « Votre situation », en français et en anglais : le
 * dictionnaire de la page, et les phrases qu'on tire d'une situation
 * (texte et surtitre de chaque alerte, phrase du dossier, valeur de chaque
 * réponse).
 *
 * Sortis de ToolsSituation.jsx pour que l'entrée de /outils
 * (ToolsSituationStart.jsx) dise la même alerte, avec les mêmes mots, sans
 * charger toute la page.
 */

import { dateLongue, euros, valeurLisible } from "../../lib/tools/dossierTextes.js";

export const T = {
    fr: {
        vide: "Après six questions, vous verrez ici ce qu'il vous reste chaque mois, où vous en êtes du seuil de TVA et votre prochaine échéance.",
        confidentiel: "Votre dossier reste sur cet appareil. Pas de compte, rien n'est envoyé.",
        commencer: "Commencer",
        modifier: "Modifier mes réponses",
        pretTitre: "Votre dossier est prêt",
        pretTexte: "{n} outils sont déjà remplis avec vos réponses. Vous n'aurez plus à les retaper.",
        pretTexteUn: "Un outil est déjà rempli avec vos réponses. Vous n'aurez plus à les retaper.",
        pretAucun: "Vous avez passé les questions : les outils se rempliront au fil de vos réponses.",
        pretRepere: "Ce que nous avons déjà repéré",
        accueilTitre: "Votre dossier",
        syntheseTitre: "Ce que vos réponses donnent",
        pretFermer: "Voir ma situation",
        pretPourVous: "par mois pour vous, frais payés",
        pretSource: "D'après vos réponses",
        pretOutils: "{n} outils déjà remplis avec vos réponses",
        pretOutilsUn: "Un outil déjà rempli avec vos réponses",
        completerTitre: "Il manque {n} réponses",
        completerTitreUn: "Il manque une réponse",
        completerIntro: "Chacune rend la page plus juste. Toutes sont facultatives.",
        completerGains: {
            statut: "Votre statut : les calculs suivent vos vraies règles",
            activite: "Votre activité : vos cotisations et vos plafonds deviennent exacts",
            dateDebut: "Votre date de début : vos échéances et l'ACRE apparaissent",
            caCetteAnnee: "Votre chiffre d'affaires : ce qu'il vous reste, la TVA et le plafond",
            foyer: "Votre foyer : l'impôt affiché devient exact",
            objectifMensuel: "Votre objectif : ce qu'il vous manque, et combien facturer",
        },
        completerPlusTard: "Plus tard",
        cestFait: "C'est fait",
        faitTitre: "C'est fait ?",
        faitTexte: "« {titre} » : nous le retirons de vos points à faire. Vous pourrez le remettre depuis « En ordre ».",
        faitOui: "Oui, c'est fait",
        pasEncore: "Pas encore",
        enOrdreTitre: "En ordre ({n})",
        enOrdreLe: "fait le {date}",
        remettre: "Remettre",
        misAJour: "Mis à jour le {date}",
        caAncienTitre: "Votre chiffre d'affaires date du {date}",
        caAncienTexte: "Toujours {montant} prévus cette année ? La date de passage du seuil de TVA en dépend.",
        caJuste: "C'est toujours juste",
        caCorriger: "Corriger",
        changementsTitre: "Ce qui a changé",
        changementReste: "Ce qu'il vous reste : {avant} → {apres} par mois",
        changementNouveau: "Nouveau : {titre}",
        changementDisparu: "Réglé : {titre}",
        fermer: "Fermer",
        exemple: "Exemple",
        accueilDuree: "6 questions, 2 minutes",
        exempleReste: "par mois pour vous",
        exempleTva: "du seuil de TVA",
        exempleEcheance: "Solde de la CFE",
        effacerTitre: "Effacer votre dossier ?",
        effacerOui: "Effacer",
        renouvelerTitre: "Changer l'adresse de l'agenda ?",
        renouvelerOui: "Changer l'adresse",
        annuler: "Annuler",
        corrigerAide: "Cliquez un élément pour le corriger.",

        phraseStatut: {
            projet: "Projet d'activité",
            micro: "Micro-entreprise",
            reel: "Entreprise individuelle au réel",
            eurl: "EURL à l'impôt sur les sociétés",
            eurlIr: "EURL à l'impôt sur le revenu",
            sasu: "SASU",
        },
        phraseActivite: { vente: "vente de marchandises", services: "prestations", bnc: "activité libérale", cipav: "profession libérale réglementée" },
        phraseDepuis: "depuis le {date}",
        phraseDemarrage: "démarrage prévu le {date}",
        phraseCa: "{montant} prévus cette année",
        phraseSeul: "Seul",
        phraseCouple: "En couple",
        sansEnfant: "sans enfant",
        unEnfant: "un enfant",
        enfants: "{n} enfants",

        aSurveiller: "À surveiller",
        aFaire: "À faire",
        aFaireAvant: "À faire avant le {date}",
        bonAsavoir: "Bon à savoir",
        voirCalcul: "Voir le calcul",
        voirCalculDans: "Voir le calcul dans « {outil} »",

        tvaTitreDate: "Vous dépasserez le seuil de TVA vers le {date}",
        tvaTexteDate: "À ce rythme, vous passez {majore} autour du {date}. La TVA est due dès ce jour-là : les factures suivantes devront l'ajouter.",
        tvaTitreJanvierPasse: "La TVA est due depuis le 1er janvier",
        tvaTexteJanvierPasse: "Votre chiffre d'affaires de l'an dernier a dépassé {base} : vos factures doivent porter la TVA depuis le 1er janvier.",
        tvaTitrePasse: "Vous avez sans doute dépassé le seuil de TVA",
        tvaTextePasse: "À ce rythme, vous avez passé {majore} vers le {date}, et la TVA est due depuis. Vérifiez avec vos encaissements réels.",
        tvaTexteDateMois: "D'après ce que vous avez encaissé mois par mois, vous passez {majore} autour du {date}. La TVA est due dès ce jour-là : les factures suivantes devront l'ajouter.",
        tvaTextePasseMois: "D'après ce que vous avez encaissé mois par mois, vous avez passé {majore} vers le {date}, et la TVA est due depuis.",
        tvaTitreJanvier: "La TVA arrive au 1er janvier",
        tvaTexteJanvier: "Votre chiffre d'affaires dépasse {base} cette année : vous facturerez la TVA à partir du 1er janvier.",
        tvaTitreProche: "Vous approchez du seuil de TVA",
        tvaTexteProche: "À votre rythme, vous atteindrez {part} du seuil de {base} cette année.",
        tvaSoins: "Si vous exercez un métier de soin, vos soins sont exonérés de TVA et ne comptent pas dans ce seuil.",

        vflTitre: "Le versement libératoire vous ferait garder {montant} de plus par an",
        vflTexte: "Il vous reste {jours} pour le demander à l'Urssaf pour {annee} (au plus tard le {date}), si votre revenu fiscal de référence le permet.",
        vflTexteDernier: "C'est le dernier jour pour le demander à l'Urssaf pour {annee}, si votre revenu fiscal de référence le permet.",
        vflTexteCreation: "Vous pouvez encore le demander jusqu'au {date}, si votre revenu fiscal de référence le permet.",
        vflTexteProjet: "Vous pourrez le demander dans les trois mois qui suivent votre création, si votre revenu fiscal de référence le permet.",
        vflVerifier: "Vérifier si j'y ai droit",
        vflRenoncerTitre: "Le barème vous ferait garder {montant} de plus par an",
        vflRenoncerTexte: "Vous êtes au versement libératoire. Y renoncer pour {annee} se demande avant le {date}.",
        impotTitre: "Votre impôt ne sera pas prélevé tout seul",
        impotTexte:
            "La première année, rien n'est prélevé sur ce que vous encaissez : environ {montant} d'impôt par an arriveraient d'un coup à partir de septembre {annee}. Pour l'étaler, ouvrez un acompte sur impots.gouv.fr, dans « Gérer mon prélèvement à la source » puis « Gérer vos acomptes ». Il sera prélevé chaque mois.",
        impotTexteDeuxieme:
            "Vos acomptes ne démarreront qu'en septembre, après votre première déclaration : l'impôt de vos revenus de janvier à août arriverait d'un coup en septembre {annee}. Pour l'éviter, ouvrez dès maintenant un acompte sur impots.gouv.fr, dans « Gérer mon prélèvement à la source » puis « Gérer vos acomptes ».",
        impotTexteProjet:
            "La première année, rien ne sera prélevé sur ce que vous encaissez : environ {montant} d'impôt par an arriveraient d'un coup, à partir de septembre de l'année suivante. Le jour de la création, ouvrez un acompte sur impots.gouv.fr, dans « Gérer mon prélèvement à la source » puis « Gérer vos acomptes ». Il sera prélevé chaque mois.",

        plafondFermeTitre: "Vous sortez de la micro au 1er janvier",
        plafondFermeTexte: "Deux années de suite au-dessus de {plafond} : vous passerez au réel au 1er janvier.",
        plafondProcheTitre: "Vous approchez du plafond de la micro",
        plafondProcheTexte: "Vous êtes à {part} du plafond de {plafond}. Il faudrait le dépasser deux années de suite pour sortir de la micro.",

        statutChangerTitre: "Changer de statut vous ferait gagner {montant} par an",
        statutChangerTexte: "Avec vos frais, il vous resterait {netMeilleur} en {meilleur}, contre {netActuel} aujourd'hui.",

        acreTitre: "Demandez l'ACRE avant le {date}",
        acreTexte: "Il vous reste {jours} : passé ce délai, l'aide est perdue.",
        cfeTitre: "Déclarez votre CFE avant le {date}",
        cfeTexte: "Le formulaire 1447-C se dépose l'année de la création.",

        resteTitre: "Ce qu'il vous reste",
        parMois: "par mois",
        resteMicro: "Après {cotisations} de cotisations et {impot} d'impôt sur l'année.",
        resteMicroFrais: "Après {cotisations} de cotisations, {impot} d'impôt et {frais} de frais sur l'année.",
        resteReel: "Après cotisations, impôt et vos frais.",
        resteSociete: "Frais payés, avec le meilleur partage entre rémunération et dividendes.",
        objectifManque: "Votre objectif est de {objectif} : il manque environ {manque} par mois une fois vos frais payés.",
        objectifAtteint: "Votre objectif de {objectif} est atteint, frais payés.",
        combienFacturer: "Combien facturer pour y arriver",
        resteManque: "Indiquez votre chiffre d'affaires pour le savoir.",
        resteManqueActivite: "Indiquez votre activité pour le savoir.",
        resteCipav: "Nous ne faisons pas ce calcul ici pour une profession réglementée dans ce statut : l'outil dédié vous dira ce qu'il est possible de calculer.",

        plafondTitre: "Plafond de la micro",
        prevus: "{montant} prévus",
        plafond: "Plafond {montant}",
        plafondAcquis: "Les deux premières années, vous restez en micro quel que soit votre chiffre d'affaires.",
        plafondLoin: "Vous restez loin du plafond. Il faudrait le dépasser deux années de suite pour sortir de la micro.",
        plafondPres: "Il faudrait le dépasser deux années de suite pour sortir de la micro.",

        tvaTitre: "Seuil de TVA",
        tvaFranchise: "Vous restez sous le seuil de {base}.",
        tvaAuDessus: "Au-dessus du seuil de {base} : le détail est dans l'alerte plus haut.",

        echeanceTitre: "Prochaine échéance",
        echeanceAucune: "Avec vos réponses, nous ne voyons aucune échéance dans les douze prochains mois.",
        echeanceManque: "Indiquez votre date de début pour voir vos échéances.",
        toutesEcheances: "Toutes vos échéances",
        dansJours: "dans {jours}",
        aujourdhui: "aujourd'hui",
        telecharger: "Ajouter à mon agenda",
        telechargerAide: "Un fichier que Google Agenda, Apple Calendrier et Outlook ouvrent, avec un rappel la veille.",
        abonner: "S'abonner à mon agenda",
        abonnerAide: "Collez cette adresse dans votre agenda (« Ajouter un agenda à partir d'une URL ») : les dates se mettront à jour seules.",
        copier: "Copier l'adresse",
        copie: "Copiée",
        renouveler: "Changer d'adresse",
        renouvelerConfirmer: "Changer l'adresse de votre agenda ? L'ancienne cessera aussitôt de fonctionner : il faudra coller la nouvelle dans votre agenda.",
        renouvele: "C'est fait : collez la nouvelle adresse dans votre agenda.",
        renouvelerErreur: "Le changement n'a pas abouti. Réessayez dans un instant.",

        statutTitre: "Changer de statut ?",
        statutGarderTitre: "Pas encore : {actuel} reste le bon choix",
        statutGarderTexte: "Avec {frais} de frais par an, il vous resterait {netActuel} en {actuelCourt}, et {netSecond} en {second}. Vous le verrez ici quand l'écart dépassera 1 000 € par an.",
        statutGarderSociete: "À ce chiffre, il vous resterait {netActuel} en {actuelCourt} et {netSecond} en {second} : l'écart ne justifie pas de changer.",
        ouvrirComparatif: "Ouvrir le comparatif",
        regimes: { micro: "micro-entreprise", reel: "entreprise individuelle au réel", eurl: "EURL", sasu: "SASU" },
        regimesArticle: { micro: "la micro-entreprise", reel: "l'entreprise individuelle au réel", eurl: "l'EURL", sasu: "la SASU" },

        reponsesTitre: "Vos réponses",
        reponsesCompte: "{n} sur 6",
        nonRenseigne: "Pas encore renseigné",
        repondre: "Répondre",
        modifierCourt: "Modifier",
        questions: {
            statut: "Statut",
            activite: "Activité",
            debut: "Début d'activité",
            ca: "Chiffre d'affaires",
            foyer: "Foyer",
            objectif: "Objectif",
        },
        objectifValeur: "{objectif} par mois, {frais} de frais",
        caValeur: "{cette} cette année",
        caValeurDeux: "{cette} cette année, {dernier} l'an dernier",

        outilsTitre: "Vos outils, déjà remplis",
        aRegarder: "À regarder",
        rienARegarder: "Rien ne presse. Tout est en ordre pour l'instant.",
        vosDates: "Vos prochaines dates",
        votreProjet: "Votre projet",
        votreActivite: "Votre activité",
        etiquettesAide: "Touchez une étiquette pour la corriger.",
        plusDeDates: "Voir les {n} autres dates",
        moinsDeDates: "Voir moins",
        tvaDejaBouton: "Je la facture déjà",
        tvaQuestion: "Facturez-vous déjà la TVA ?",
        tvaOui: "Oui, déjà",
        tvaNon: "Pas encore",
        tvaDejaTitre: "Déjà facturée",
        tvaDejaTexte: "Vous facturez la TVA à vos clients : le seuil n'est plus à surveiller.",
        rempli: "Rempli",

        identiteTitre: "Votre identité, pour les formulaires",
        identiteVide: "Enregistrez-la une fois : elle remplira les douze formulaires (attestations, déclarations, devis, mise en demeure). Elle reste sur cet appareil, même connecté.",
        identiteEnregistrer: "Enregistrer mon identité",
        identiteModifier: "Modifier",

        compteTitre: "Retrouvez votre dossier partout",
        compteCreer: "Recevoir mon lien",
        identiteGardee: "Gardée sur cet appareil, jamais envoyée. Elle remplit les douze formulaires.",
        identiteChamps: { nom: "Nom", adresse: "Adresse", naissance: "Naissance", siret: "SIRET", contact: "Contact" },
        compteAvantages: ["Votre dossier sur tous vos appareils", "Les alertes chiffrées pour votre situation", "Un agenda qui se met à jour seul"],
        compteSansMotDePasse: "Gratuit, sans mot de passe : un lien par e-mail.",
        compteSynchro: "Votre dossier suit votre compte : vous le retrouvez sur tous vos appareils.",

        alerteTitre: "Prévenez-moi si une règle change pour ma situation",
        alerteTexte: "Un taux, un seuil ou un plafond qui bouge : nous vous écrivons avec le calcul refait pour votre dossier.",
        alerteBouton: "Me prévenir",
        alerteEnvoi: "Inscription…",
        alerteA: "Les alertes partiront à {email}.",
        alerteOk: "C'est noté : vous serez prévenu à {email}.",
        alerteErreur: "L'inscription n'a pas abouti. Réessayez dans un instant.",

        pied: "Calculs indicatifs, barèmes 2026. Seuls votre avis d'imposition et l'administration font foi.",
        effacer: "Effacer mon dossier",
        effacerConfirmer: "Effacer votre dossier ? Les outils ne se rempliront plus. Votre identité, gardée pour les formulaires, n'est pas effacée : vous pouvez l'effacer depuis sa propre fenêtre.",
    },
    en: {
        vide: "After six questions, you will see here what you keep each month, how close you are to the VAT threshold and your next deadline.",
        confidentiel: "Your profile stays on this device. No account, nothing is sent.",
        commencer: "Start",
        modifier: "Edit my answers",
        pretTitre: "Your profile is ready",
        pretTexte: "{n} tools are already filled in with your answers. You won't have to type them again.",
        pretTexteUn: "One tool is already filled in with your answers. You won't have to type them again.",
        pretAucun: "You skipped the questions: the tools will fill themselves in as you answer.",
        pretRepere: "What we have already spotted",
        accueilTitre: "Your profile",
        syntheseTitre: "What your answers show",
        pretFermer: "See my situation",
        pretPourVous: "a month for you, expenses paid",
        pretSource: "Based on your answers",
        pretOutils: "{n} tools already filled in with your answers",
        pretOutilsUn: "One tool already filled in with your answers",
        completerTitre: "{n} answers missing",
        completerTitreUn: "One answer missing",
        completerIntro: "Each one makes the page more accurate. All are optional.",
        completerGains: {
            statut: "Your status: the calculations follow your actual rules",
            activite: "Your activity: your contributions and ceilings become exact",
            dateDebut: "Your start date: your deadlines and the ACRE appear",
            caCetteAnnee: "Your revenue: what you keep, VAT and the ceiling",
            foyer: "Your household: the income tax shown becomes exact",
            objectifMensuel: "Your goal: what is missing, and how much to charge",
        },
        completerPlusTard: "Later",
        cestFait: "Done",
        faitTitre: "Done?",
        faitTexte: "“{titre}”: we remove it from your to-do points. You can bring it back from “Sorted”.",
        faitOui: "Yes, it's done",
        pasEncore: "Not yet",
        enOrdreTitre: "Sorted ({n})",
        enOrdreLe: "done on {date}",
        remettre: "Bring back",
        misAJour: "Updated on {date}",
        caAncienTitre: "Your revenue dates from {date}",
        caAncienTexte: "Still {montant} expected this year? The date you cross the VAT threshold depends on it.",
        caJuste: "Still right",
        caCorriger: "Correct it",
        changementsTitre: "What changed",
        changementReste: "What you keep: {avant} → {apres} a month",
        changementNouveau: "New: {titre}",
        changementDisparu: "Sorted: {titre}",
        fermer: "Close",
        exemple: "Example",
        accueilDuree: "6 questions, 2 minutes",
        exempleReste: "a month for you",
        exempleTva: "of the VAT threshold",
        exempleEcheance: "CFE balance",
        effacerTitre: "Erase your profile?",
        effacerOui: "Erase",
        renouvelerTitre: "Change the calendar address?",
        renouvelerOui: "Change the address",
        annuler: "Cancel",
        corrigerAide: "Click an item to correct it.",

        phraseStatut: {
            projet: "Business project",
            micro: "Micro-enterprise",
            reel: "Sole trader on actual expenses",
            eurl: "EURL paying corporation tax",
            eurlIr: "EURL taxed as a sole trader",
            sasu: "SASU",
        },
        phraseActivite: { vente: "selling goods", services: "services", bnc: "liberal profession", cipav: "regulated liberal profession" },
        phraseDepuis: "since {date}",
        phraseDemarrage: "planned start on {date}",
        phraseCa: "{montant} expected this year",
        phraseSeul: "Single",
        phraseCouple: "As a couple",
        sansEnfant: "no children",
        unEnfant: "one child",
        enfants: "{n} children",

        aSurveiller: "Watch out",
        aFaire: "To do",
        aFaireAvant: "To do before {date}",
        bonAsavoir: "Good to know",
        voirCalcul: "See the calculation",
        voirCalculDans: "See the calculation in “{outil}”",

        tvaTitreDate: "You will cross the VAT threshold around {date}",
        tvaTexteDate: "At this pace, you pass {majore} around {date}. VAT is due from that day: the following invoices must add it.",
        tvaTitreJanvierPasse: "VAT has been due since 1 January",
        tvaTexteJanvierPasse: "Last year's revenue exceeded {base}: your invoices have had to include VAT since 1 January.",
        tvaTitrePasse: "You have probably crossed the VAT threshold",
        tvaTextePasse: "At this pace, you passed {majore} around {date}, and VAT has been due since. Check against your actual takings.",
        tvaTexteDateMois: "Based on what you brought in month by month, you pass {majore} around {date}. VAT is due from that day: the following invoices must add it.",
        tvaTextePasseMois: "Based on what you brought in month by month, you passed {majore} around {date}, and VAT has been due since.",
        tvaTitreJanvier: "VAT starts on 1 January",
        tvaTexteJanvier: "Your revenue exceeds {base} this year: you will charge VAT from 1 January.",
        tvaTitreProche: "You are getting close to the VAT threshold",
        tvaTexteProche: "At your pace, you will reach {part} of the {base} threshold this year.",
        tvaSoins: "If you work in healthcare, your care is VAT-exempt and does not count towards this threshold.",

        vflTitre: "The flat-rate income tax payment would let you keep {montant} more a year",
        vflTexte: "You have {jours} left to apply to the Urssaf for {annee} (by {date} at the latest), if your reference tax income allows it.",
        vflTexteDernier: "Today is the last day to apply to the Urssaf for {annee}, if your reference tax income allows it.",
        vflTexteCreation: "You can still apply until {date}, if your reference tax income allows it.",
        vflTexteProjet: "You can apply within three months of starting, if your reference tax income allows it.",
        vflVerifier: "Check whether I'm eligible",
        vflRenoncerTitre: "The standard scale would let you keep {montant} more a year",
        vflRenoncerTexte: "You use the flat-rate payment. Giving it up for {annee} must be requested before {date}.",
        impotTitre: "Your income tax won't be withheld on its own",
        impotTexte:
            "In the first year, nothing is withheld on what you bring in: about {montant} of tax a year would land all at once from September {annee}. To spread it, open an instalment on impots.gouv.fr, under \"Gérer mon prélèvement à la source\" then \"Gérer vos acomptes\". It will be taken every month.",
        impotTexteDeuxieme:
            "Your instalments will only start in September, after your first tax return: the tax on your income from January to August would land all at once in September {annee}. To avoid it, open an instalment now on impots.gouv.fr, under \"Gérer mon prélèvement à la source\" then \"Gérer vos acomptes\".",
        impotTexteProjet:
            "In the first year, nothing will be withheld on what you bring in: about {montant} of tax a year would land all at once, from September of the following year. On the day you register, open an instalment on impots.gouv.fr, under \"Gérer mon prélèvement à la source\" then \"Gérer vos acomptes\". It will be taken every month.",

        plafondFermeTitre: "You leave the micro-enterprise on 1 January",
        plafondFermeTexte: "Two years in a row above {plafond}: you will switch to actual expenses on 1 January.",
        plafondProcheTitre: "You are getting close to the micro-enterprise ceiling",
        plafondProcheTexte: "You are at {part} of the {plafond} ceiling. You would have to exceed it two years in a row to leave the micro-enterprise.",

        statutChangerTitre: "Changing status would earn you {montant} a year",
        statutChangerTexte: "With your expenses, you would keep {netMeilleur} as {meilleur}, against {netActuel} today.",

        acreTitre: "Apply for the ACRE before {date}",
        acreTexte: "You have {jours} left: after that, the relief is lost.",
        cfeTitre: "File your CFE return before {date}",
        cfeTexte: "Form 1447-C is filed in the year of creation.",

        resteTitre: "What you keep",
        parMois: "a month",
        resteMicro: "After {cotisations} of contributions and {impot} of income tax over the year.",
        resteMicroFrais: "After {cotisations} of contributions, {impot} of income tax and {frais} of expenses over the year.",
        resteReel: "After contributions, income tax and your expenses.",
        resteSociete: "Expenses paid, with the best split between salary and dividends.",
        objectifManque: "Your goal is {objectif}: about {manque} a month is missing once your expenses are paid.",
        objectifAtteint: "Your goal of {objectif} is reached, expenses paid.",
        combienFacturer: "How much to charge to get there",
        resteManque: "Add your revenue to find out.",
        resteManqueActivite: "Add your activity to find out.",
        resteCipav: "We don't run this calculation here for a regulated profession under this status: the dedicated tool will tell you what can be calculated.",

        plafondTitre: "Micro-enterprise ceiling",
        prevus: "{montant} expected",
        plafond: "Ceiling {montant}",
        plafondAcquis: "For your first two years, you stay in the micro-enterprise whatever your revenue.",
        plafondLoin: "You are far from the ceiling. You would have to exceed it two years in a row to leave the micro-enterprise.",
        plafondPres: "You would have to exceed it two years in a row to leave the micro-enterprise.",

        tvaTitre: "VAT threshold",
        tvaFranchise: "You stay below the {base} threshold.",
        tvaAuDessus: "Above the {base} threshold: the details are in the alert above.",

        echeanceTitre: "Next deadline",
        echeanceAucune: "With your answers, we see no deadline in the next twelve months.",
        echeanceManque: "Add your start date to see your deadlines.",
        toutesEcheances: "All your deadlines",
        dansJours: "in {jours}",
        aujourdhui: "today",
        telecharger: "Add to my calendar",
        telechargerAide: "A file that Google Calendar, Apple Calendar and Outlook open, with a reminder the day before.",
        abonner: "Subscribe in my calendar",
        abonnerAide: "Paste this address into your calendar (“Add calendar from URL”): the dates will update on their own.",
        copier: "Copy the address",
        copie: "Copied",
        renouveler: "Change the address",
        renouvelerConfirmer: "Change your calendar address? The old one stops working at once: you will need to paste the new one into your calendar.",
        renouvele: "Done: paste the new address into your calendar.",
        renouvelerErreur: "The change did not go through. Try again in a moment.",

        statutTitre: "Change status?",
        statutGarderTitre: "Not yet: {actuel} is still the right choice",
        statutGarderTexte: "With {frais} of expenses a year, you would keep {netActuel} as {actuelCourt}, and {netSecond} as {second}. You will see it here when the gap exceeds €1,000 a year.",
        statutGarderSociete: "At this figure, you would keep {netActuel} as {actuelCourt} and {netSecond} as {second}: the gap does not justify a change.",
        ouvrirComparatif: "Open the comparison",
        regimes: { micro: "a micro-enterprise", reel: "a sole trader on actual expenses", eurl: "an EURL", sasu: "a SASU" },
        regimesArticle: { micro: "the micro-enterprise", reel: "sole trading on actual expenses", eurl: "the EURL", sasu: "the SASU" },

        reponsesTitre: "Your answers",
        reponsesCompte: "{n} of 6",
        nonRenseigne: "Not filled in yet",
        repondre: "Answer",
        modifierCourt: "Edit",
        questions: {
            statut: "Status",
            activite: "Activity",
            debut: "Start of activity",
            ca: "Revenue",
            foyer: "Household",
            objectif: "Goal",
        },
        objectifValeur: "{objectif} a month, {frais} of expenses",
        caValeur: "{cette} this year",
        caValeurDeux: "{cette} this year, {dernier} last year",

        outilsTitre: "Your tools, already filled in",
        aRegarder: "To look at",
        rienARegarder: "Nothing urgent. Everything is in order for now.",
        vosDates: "Your upcoming dates",
        votreProjet: "Your project",
        votreActivite: "Your business",
        etiquettesAide: "Tap a label to correct it.",
        plusDeDates: "See the {n} other dates",
        moinsDeDates: "See less",
        tvaDejaBouton: "I already charge it",
        tvaQuestion: "Do you already charge VAT?",
        tvaOui: "Yes, already",
        tvaNon: "Not yet",
        tvaDejaTitre: "Already charged",
        tvaDejaTexte: "You charge VAT to your clients: the threshold no longer needs watching.",
        rempli: "Filled in",

        identiteTitre: "Your identity, for the forms",
        identiteVide: "Save it once: it will fill in the twelve forms (certificates, returns, quotes, formal notice). It stays on this device, even when signed in.",
        identiteEnregistrer: "Save my identity",
        identiteModifier: "Edit",

        compteTitre: "Find your profile everywhere",
        compteCreer: "Send me my link",
        identiteGardee: "Kept on this device, never sent. It fills in the twelve forms.",
        identiteChamps: { nom: "Name", adresse: "Address", naissance: "Birth", siret: "SIRET", contact: "Contact" },
        compteAvantages: ["Your profile on all your devices", "Alerts worked out for your situation", "A calendar that updates itself"],
        compteSansMotDePasse: "Free, no password: a link by email.",
        compteSynchro: "Your profile follows your account: you find it on all your devices.",

        alerteTitre: "Tell me if a rule changes for my situation",
        alerteTexte: "A rate, a threshold or a ceiling that moves: we write to you with the calculation redone for your profile.",
        alerteBouton: "Notify me",
        alerteEnvoi: "Signing up…",
        alerteA: "Alerts will go to {email}.",
        alerteOk: "Done: you will be notified at {email}.",
        alerteErreur: "The sign-up did not go through. Try again in a moment.",

        pied: "Indicative calculations, 2026 rates. Only your tax notice and the administration are authoritative.",
        effacer: "Erase my profile",
        effacerConfirmer: "Erase your profile? The tools will no longer fill themselves in. Your identity, kept for the forms, is not erased: you can erase it from its own window.",
    },
};

export function remplir(texte, valeurs) {
    return String(texte).replace(/\{(\w+)\}/g, (_, k) => (valeurs[k] !== undefined && valeurs[k] !== null ? valeurs[k] : ""));
}

export function pourcent(part, lang) {
    return new Intl.NumberFormat(lang === "en" ? "en-GB" : "fr-FR", { style: "percent", maximumFractionDigits: 0 }).format(part);
}

export function nJours(n, lang) {
    if (lang === "en") return n === 1 ? "1 day" : `${n} days`;
    return n === 1 ? "1 jour" : `${n} jours`;
}

/** Une date sans l'année quand elle tombe dans l'année en cours. */
export function dateCourte(iso, lang, anneeCourante) {
    const long = dateLongue(iso, lang);
    return Number(iso.slice(0, 4)) === anneeCourante ? long.replace(/\s\d{4}$/, "") : long;
}


/** Le texte d'une alerte, selon son type : titre, corps, lien. */
export function texteAlerte(a, r, t, lang) {
    const annee = Number(r.aujourdhui.slice(0, 4));
    const court = (iso) => dateCourte(iso, lang, annee);
    if (a.cle === "tva") {
        const v = r.tva;
        const suffixe = v.soins ? ` ${t.tvaSoins}` : "";
        if (v.statut === "assujetti_depuis_janvier") {
            return { titre: t.tvaTitreJanvierPasse, texte: remplir(t.tvaTexteJanvierPasse, { base: euros(v.base, lang) }) + suffixe, lien: t.voirCalcul };
        }
        if (v.statut === "assujetti_immediat" && v.passe) {
            const texte = v.depuisEncaissements ? t.tvaTextePasseMois : t.tvaTextePasse;
            return { titre: t.tvaTitrePasse, texte: remplir(texte, { majore: euros(v.majore, lang), date: court(v.date) }) + suffixe, lien: t.voirCalcul };
        }
        if (v.statut === "assujetti_immediat") {
            return {
                titre: remplir(t.tvaTitreDate, { date: court(v.date) }),
                texte: remplir(v.depuisEncaissements ? t.tvaTexteDateMois : t.tvaTexteDate, { majore: euros(v.majore, lang), date: court(v.date) }) + suffixe,
                lien: t.voirCalcul,
            };
        }
        if (v.statut === "bascule_janvier_prochain") {
            return { titre: t.tvaTitreJanvier, texte: remplir(t.tvaTexteJanvier, { base: euros(v.base, lang) }) + suffixe, lien: t.voirCalcul };
        }
        return {
            titre: t.tvaTitreProche,
            texte: remplir(t.tvaTexteProche, { part: pourcent(r.dossier.caCetteAnnee / v.base, lang), base: euros(v.base, lang) }) + suffixe,
            lien: t.voirCalcul,
        };
    }
    if (a.cle === "vfl") {
        const v = r.vfl;
        let texte;
        if (v.creation && !v.limite) texte = t.vflTexteProjet;
        else if (v.creation) texte = remplir(t.vflTexteCreation, { date: court(v.limite) });
        else if (v.joursRestants <= 0) texte = remplir(t.vflTexteDernier, { annee: v.pourAnnee });
        else texte = remplir(t.vflTexte, { jours: nJours(v.joursRestants, lang), annee: v.pourAnnee, date: court(v.limite) });
        return { titre: remplir(t.vflTitre, { montant: euros(v.gain, lang) }), texte, lien: t.vflVerifier, outil: "eligibilite-versement-liberatoire" };
    }
    if (a.cle === "vfl_renoncer") {
        const v = r.vfl;
        return {
            titre: remplir(t.vflRenoncerTitre, { montant: euros(v.gain, lang) }),
            texte: remplir(t.vflRenoncerTexte, { annee: v.pourAnnee, date: court(v.limite) }),
            lien: t.voirCalcul,
        };
    }
    if (a.cle === "plafond") {
        const p = r.plafond;
        return p.fermeture
            ? { titre: t.plafondFermeTitre, texte: remplir(t.plafondFermeTexte, { plafond: euros(p.plafond, lang) }), lien: t.voirCalcul }
            : { titre: t.plafondProcheTitre, texte: remplir(t.plafondProcheTexte, { part: pourcent(p.part, lang), plafond: euros(p.plafond, lang) }), lien: t.voirCalcul };
    }
    if (a.cle === "statut") {
        const s = r.statutConseil;
        return {
            titre: remplir(t.statutChangerTitre, { montant: euros(s.gain, lang) }),
            texte: remplir(t.statutChangerTexte, {
                netMeilleur: euros(s.nets[s.meilleur], lang),
                meilleur: t.regimes[s.meilleur],
                netActuel: euros(s.nets[s.actuel], lang),
            }),
            lien: t.ouvrirComparatif,
        };
    }
    if (a.cle === "echeance_acre-demande") {
        return {
            titre: remplir(t.acreTitre, { date: court(a.echeance.date) }),
            texte: remplir(t.acreTexte, { jours: nJours(a.echeance.joursRestants, lang) }),
            lien: t.voirCalcul,
        };
    }
    if (a.cle === "impot_acomptes") {
        return {
            titre: t.impotTitre,
            texte: remplir(r.dossier.statut === "projet" ? t.impotTexteProjet : a.deuxiemeAnnee ? t.impotTexteDeuxieme : t.impotTexte, {
                montant: euros(Math.round(r.revenu.impot), lang),
                annee: annee + 1,
            }),
            lien: t.voirCalcul,
        };
    }
    if (a.cle === "echeance_cfe-declaration") {
        return { titre: remplir(t.cfeTitre, { date: court(a.echeance.date) }), texte: t.cfeTexte, lien: t.voirCalcul };
    }
    return null;
}

/** Le surtitre d'une alerte : ce qu'il faut en faire, et avant quand, quand c'est daté. */
export function kickerAlerte(a, r, t, lang) {
    if (a.gravite === "info") return t.bonAsavoir;
    const annee = Number(r.aujourdhui.slice(0, 4));
    const limite = a.cle === "vfl" || a.cle === "vfl_renoncer" ? r.vfl?.limite : a.echeance?.date;
    if (limite) return remplir(t.aFaireAvant, { date: dateCourte(limite, lang, annee) });
    return a.cle.startsWith("echeance") || a.cle.startsWith("vfl") ? t.aFaire : t.aSurveiller;
}

/** La phrase d'en-tête : « Activité libérale en micro-entreprise depuis le 2 mars 2025. » */
export function phraseDossier(d, t, lang) {
    const morceaux = [];
    const premiere = [];
    if (d.statut) premiere.push(t.phraseStatut[d.statut]);
    if (d.activite) premiere.push(d.statut ? `(${t.phraseActivite[d.activite]})` : t.phraseActivite[d.activite]);
    if (d.dateDebut) premiere.push(remplir(d.statut === "projet" ? t.phraseDemarrage : t.phraseDepuis, { date: dateLongue(d.dateDebut, lang) }));
    if (premiere.length) morceaux.push(premiere.join(" "));
    if (d.caCetteAnnee !== null) morceaux.push(remplir(t.phraseCa, { montant: euros(d.caCetteAnnee, lang) }));
    if (d.enCouple !== null) {
        const enfants = d.enfants === null ? null : d.enfants === 0 ? t.sansEnfant : d.enfants === 1 ? t.unEnfant : remplir(t.enfants, { n: d.enfants });
        morceaux.push([d.enCouple ? t.phraseCouple : t.phraseSeul, enfants].filter(Boolean).join(", "));
    }
    return morceaux.map((m) => `${m.charAt(0).toUpperCase()}${m.slice(1)}.`).join(" ");
}

/** La valeur de chaque question, pour le récapitulatif. */
export function valeurQuestion(q, d, t, lang) {
    if (q === "statut") return d.statut ? valeurLisible("statut", d.statut, lang) : null;
    if (q === "activite") {
        if (!d.activite) return null;
        const base = valeurLisible("activite", d.activite, lang);
        return d.activite === "services" && d.artisan ? `${base} (${lang === "en" ? "craftsperson" : "artisan"})` : base;
    }
    if (q === "debut") return d.dateDebut ? dateLongue(d.dateDebut, lang) : null;
    if (q === "ca") {
        if (d.caCetteAnnee === null) return null;
        return d.caAnDernier !== null
            ? remplir(t.caValeurDeux, { cette: euros(d.caCetteAnnee, lang), dernier: euros(d.caAnDernier, lang) })
            : remplir(t.caValeur, { cette: euros(d.caCetteAnnee, lang) });
    }
    if (q === "foyer") {
        if (d.enCouple === null && d.enfants === null) return null;
        return phraseDossier({ ...d, statut: null, activite: null, dateDebut: null, caCetteAnnee: null }, t, lang).replace(/\.$/, "");
    }
    if (q === "objectif") {
        if (d.objectifMensuel === null) return null;
        return remplir(t.objectifValeur, { objectif: euros(d.objectifMensuel, lang), frais: euros(d.fraisMensuels || 0, lang) });
    }
    return null;
}

/**
 * La phrase d'en-tête en morceaux cliquables : chacun ouvre la question qui
 * le corrige. « Micro-entreprise (activité libérale) » → statut, « depuis le
 * 2 mars 2025 » → date de début, etc.
 */
export function morceauxDossier(d, t, lang) {
    const morceaux = [];
    if (d.statut) morceaux.push({ question: "statut", texte: t.phraseStatut[d.statut] });
    if (d.activite) morceaux.push({ question: "activite", texte: d.statut ? `(${t.phraseActivite[d.activite]})` : t.phraseActivite[d.activite] });
    if (d.dateDebut) {
        morceaux.push({
            question: "debut",
            texte: remplir(d.statut === "projet" ? t.phraseDemarrage : t.phraseDepuis, { date: dateLongue(d.dateDebut, lang) }),
        });
    }
    if (d.caCetteAnnee !== null) morceaux.push({ question: "ca", texte: remplir(t.phraseCa, { montant: euros(d.caCetteAnnee, lang) }), phrase: true });
    if (d.enCouple !== null) {
        const enfants = d.enfants === null ? null : d.enfants === 0 ? t.sansEnfant : d.enfants === 1 ? t.unEnfant : remplir(t.enfants, { n: d.enfants });
        morceaux.push({ question: "foyer", texte: [d.enCouple ? t.phraseCouple : t.phraseSeul, enfants].filter(Boolean).join(", "), phrase: true });
    }
    return morceaux;
}
